/**
 * 当下重点 —— 阶段感知主视觉卡片
 *
 * 设计目标：成为 hero 之后的第二个视觉锚点。
 *  - 整张卡可点击 → 跳到对应栏目
 *  - 古铜金标签 + serif 大字栏目名 + 强引导箭头
 *  - 深蓝细边 + 阴影 + 大留白，明确"这是要点"
 */
import type { StageInfo, LifeStage } from '@/domain/stage'
import { track } from '@/lib/track'

interface Guidance {
  focusTitle: string
  focusAnchor: string      // 跳转目标，如 'section-03'
  message: string
  icon: string             // 单字图标
}

const STAGE_GUIDANCE: Record<LifeStage, Guidance> = {
  pre_exam: {
    icon: '📖',
    focusTitle: '03 · 考前注意事项',
    focusAnchor: 'section-03',
    message: '中考还有一段时间。志愿填报工具会在公告发布后开放，目前家长重点是「考前注意事项」。'
  },
  near_exam: {
    icon: '📖',
    focusTitle: '03 · 考前注意事项',
    focusAnchor: 'section-03',
    message: '考试临近。建议看完「考前注意事项」，然后关注公众号等候出分提醒。'
  },
  in_exam: {
    icon: '✏️',
    focusTitle: '03 · 考前注意事项',
    focusAnchor: 'section-03',
    message: '孩子在考试中。家长此刻不必反复刷新工具，但可以浏览栏目 03 的考场篇章。'
  },
  wait_score: {
    icon: '📊',
    focusTitle: '02 · 历年录取数据',
    focusAnchor: 'section-02',
    message: '分数和分段表发布前，建议先看「02 历年录取数据」对今年录取大致心中有数，正式数据发布后再复测。'
  },
  after_score: {
    icon: '🎯',
    focusTitle: '01 · 邹城市中考志愿填报',
    focusAnchor: 'section-01',
    message: '今年中考分数和官方分段表已发布。请优先使用「01 邹城市中考志愿填报」工具测算五校录取概率与建议。'
  },
  fill_window: {
    icon: '⏳',
    focusTitle: '01 · 邹城市中考志愿填报',
    focusAnchor: 'section-01',
    message: '志愿填报中。建议反复使用「01 邹城市中考志愿填报」工具核对填报顺位；关注公众号收取截止提醒。'
  },
  after_admission: {
    icon: '🎓',
    focusTitle: '04 · 高中学习预警  /  05 · 暑期预习安排',
    focusAnchor: 'section-04',
    message: '录取已经确定。接下来这两个月，重点看栏目 04 和 05——它们决定了孩子高一开学是从容还是吃力。'
  }
}

export function StageGuidance({
  stageInfo,
  hasResult
}: {
  stageInfo: StageInfo
  hasResult: boolean
}) {
  const g = STAGE_GUIDANCE[stageInfo.stage]
  if (!g) return null
  if (hasResult) return null

  return (
    <a
      href={`#${g.focusAnchor}`}
      onClick={() => {
        track('drill_click', {
          target: 'jump_section',
          from: 'stage_guidance',
          anchor: g.focusAnchor,
          stage: stageInfo.stage
        })
      }}
      className="
        block relative overflow-hidden
        rounded-2xl
        bg-gradient-to-br from-accent-50 via-white to-brand-50
        border-2 border-accent-200
        shadow-card
        active:scale-[0.99] hover:shadow-lg
        transition
      "
    >
      {/* 左侧深蓝竖条 */}
      <div className="absolute left-0 top-0 bottom-0 w-1.5 bg-brand-800" />

      <div className="pl-5 pr-4 py-5">
        {/* 顶部小标签 */}
        <div className="flex items-center justify-between mb-3">
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-accent-700 text-white text-[11px] tracking-wider rounded-full font-medium">
            <span>★</span> 当下重点
          </span>
          <span className="text-ink-400 text-xs">{stageInfo.label}</span>
        </div>

        {/* 主标题：建议优先查看 → 栏目名 */}
        <div className="flex items-start gap-3 mb-3">
          <div className="text-3xl leading-none mt-0.5">{g.icon}</div>
          <div className="flex-1 min-w-0">
            <div className="text-ink-500 text-xs mb-1">建议优先查看</div>
            <div className="font-serif text-brand-900 text-[19px] leading-snug font-semibold flex items-center gap-1.5">
              <span>{g.focusTitle}</span>
              <span className="text-brand-700">→</span>
            </div>
          </div>
        </div>

        {/* 解释文字 */}
        <p className="text-ink-700 text-sm leading-relaxed pl-[44px]">
          {g.message}
        </p>
      </div>
    </a>
  )
}
